/**
 * Semester classification for university academic events.
 *
 * Indonesian/Malaysian universities run an odd (Ganjil, ~Aug–Jan) and an even
 * (Genap, ~Feb–Jul) semester. Grouping events by a single date boundary at
 * February of the second calendar year mis-groups transition-window events —
 * even-semester registration happens in January and the odd semester can end in
 * February, so each falls on the "wrong" side of a date split. Academic sources
 * tag the semester in the (English) event title, so classify by title first and
 * only fall back to the date when the title has no marker (breaks, orientation,
 * graduation). Refs: almanak-bghw.
 */

export type Semester = 'odd' | 'even';

/**
 * Derive the semester from an event title, or null if it carries no marker.
 * Recognises "Odd/Even Semester", "Ganjil/Genap", "Semester 1/2", "Semester
 * I/II", and "First/Second Semester". Even is checked first so "Semester II"
 * and "Second Semester" win over the odd patterns they contain.
 */
export function semesterFromTitle(title: string): Semester | null {
  const t = title.toLowerCase();
  if (/\b(?:even|genap|second|2nd)\b/.test(t) || /\bsemester\s*(?:2|ii)\b/.test(t)) return 'even';
  if (/\b(?:odd|ganjil|first|1st)\b/.test(t) || /\bsemester\s*(?:1|i)\b/.test(t)) return 'odd';
  return null;
}

/**
 * Classify an academic event into its semester: by title when marked, else by a
 * date split at February of the year after the academic-year start.
 */
export function classifyEventSemester(
  event: { title: string; dateStart: string },
  academicYear: number,
): Semester {
  return semesterFromTitle(event.title) ?? (event.dateStart < `${academicYear + 1}-02-01` ? 'odd' : 'even');
}
